import Vue from 'vue'
import ApiService from '@/common/api.service'

export const state = {
  /*
   * groupSelectionSets is a "grouping" of multiple groups that are
   * selected. For example, one might wish to have a "global" set of
   * selected groups that is persistent across all screens or one
   * might want to create a set of selected groups for a particular
   * individual/group of widgets.
   */
  groupSelectionSets: {},
  queryByClient: null,
  groupType: ''
}

export const getters = {
  /**
   * Returns the full state object
   *
   * @param state Object
   *
   * @return Object
   */
  get (state) {
    return state
  },

  /**
   * Returns the group type (either 'vehicle' or 'unit').
   *
   * @param state
   *
   * @returns {String}
   */
  getGroupType (state) {
    return state.groupType
  },

  /**
   * Returns the sets of selected vehicles.
   *
   * @param state Object
   *
   * @return Object
   */
  groupSelectionSets (state) {
    return state.groupSelectionSets
  },

  /**
   * Returns either a client object or null depending on whether
   * queries should be made by client.
   *
   * @param state
   *
   * @return {Object|null}
   */
  getClient (state) {
    return state.queryByClient
  }
}

export const actions = {
  /**
   * Query/find one or more vehicle groups.
   *
   * @param context Object
   * @param params  Array of query key's and values
   *
   * @return Promise
   */
  query (context, params) {
    const groupType = context.getters['getGroupType']
    const queryByClient = context.getters['getClient']
    if (queryByClient !== null) {
      return ApiService
        .get(`${groupType}s/groups/by-client/${queryByClient.id}`, params)
        .then((response) => {
          return response
        })
    } else {
      return ApiService
        .get(`${groupType}s/groups`, params)
        .then((response) => {
          return response
        })
    }
  }
}

export const mutations = {

  /**
   * Add a map or array of object vehicle groups that have been selected within
   * the specified set.
   *
   * @param state    Object
   * @param set      String
   * @param groups Array|Map
   */
  addSelectedGroups (state, { set, groups }) {
    if (!state.groupSelectionSets.hasOwnProperty(set)) {
      Vue.set(state.groupSelectionSets, set, {})
    }

    groups.forEach(group => {
      Vue.set(state.groupSelectionSets[set], group.id, group)
    })
  },

  /**
   * Removes an array of vehicle groups from the set's list of selected groups.
   * The array must consist of the id's of the groups that are no
   * longer selected.
   *
   * @param state    Object
   * @param set      String
   * @param vehicles Array
   */
  removeSelectedGroups (state, { set, groups }) {
    if (state.groupSelectionSets.hasOwnProperty(set)) {
      groups.forEach(group => {
        Vue.delete(state.groupSelectionSets[set], group.id)
      })
    }
  },

  /**
   * Clears all selected groups.
   *
   * @param state  Object
   */
  clearAllSelectedGroups (state) {
    Vue.set(state, 'groupSelectionSets', {})
  },

  /**
   * Clears all selected groups within a specified set.
   *
   * @param state  Object
   * @param set      String
   */
  clearSelectedGroups (state, set) {
    if (state.groupSelectionSets.hasOwnProperty(set)) {
      Vue.set(state.groupSelectionSets, set, {})
    }
  },

  /**
   * Clears all groups from being tracked.
   *
   * @param state Object
   */
  purge (state) {
    state.groupSelectionSets = {}
    state.queryByClient = null
    state.groupType = ''
  },

  /**
   * Sets the client for which the vehicles should be queried.
   *
   * @param state
   *
   * @param client Object|null
   */
  setClient (state, client) {
    state.queryByClient = client
  },

  /**
   * Sets the group type, either 'unit' or 'vehicle'.
   *
   * @param state
   * @param groupType {String}
   */
  setGroupType (state, groupType) {
    state.groupType = groupType
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}



// WEBPACK FOOTER //
// ./src/store/groups/findGroupsWidgetModule.js